import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, CircularProgress, Button, Container, Alert,
  TextField, MenuItem, Stack, FormControl, InputLabel, Select,
  Chip, Card, CardContent, Divider
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import penaltyService from '../services/penaltyService';
import DateRangePicker from '../components/common/DateRangePicker';
import '../components/financial/PaymentHistoryInfinite.css';

// --------------------------------------------------------------------------
// PenaltyListCard — inline card component for each penalty
// --------------------------------------------------------------------------
const STATUS_COLORS = {
  pending:            'warning',
  draft:              'default',
  scheduled:          'info',
  applied:            'error',
  failed:             'error',
  disputed:           'warning',
  waived:             'success',
  collected:          'error',
  cancelled:          'default',
  refunded:           'success',
  pending_collection: 'warning',
};

const PenaltyListCard = ({ penalty, currentUserId, onOpen }) => {
  const { t } = useTranslation(['penalties', 'common']);

  const isSanctioned  = penalty?.user_id === currentUserId;
  const roleLabel     = isSanctioned
    ? t('penalties:roles.sanctioned', 'Sanctionné')
    : t('penalties:roles.beneficiary', 'Bénéficiaire');
  const roleColor     = isSanctioned ? 'error' : 'success';

  const statusKey     = penalty?.status || 'pending';
  const statusLabel   = t(`penalties:status.${statusKey}`, statusKey);
  const statusColor   = STATUS_COLORS[statusKey] || 'default';

  const penaltyTypeKey = penalty?.penalty_type || '';
  const typeLabel      = t(`penalties:types.${penaltyTypeKey}`, penaltyTypeKey.replace(/_/g, ' '));

  const amount      = penalty?.amount != null
    ? `${Number(penalty.amount).toFixed(2)} ${penalty.currency || 'EUR'}`
    : '—';

  const listingTitle = penalty?.reservation?.listing?.title
    ?? penalty?.reservation?.Listing?.title
    ?? null;

  const createdAt = penalty?.created_at
    ? new Date(penalty.created_at).toLocaleDateString()
    : '—';

  const shortId = penalty?.id ? penalty.id.slice(0, 8) : '—';

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, cursor: onOpen ? 'pointer' : 'default' }}
      onClick={() => onOpen && onOpen(penalty)}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
          {/* Left: type + ID */}
          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
              {typeLabel}
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                #{shortId}
              </Typography>
            </Typography>
            {listingTitle && (
              <Typography variant="body2" color="text.secondary">
                {t('penalties:listing', 'Annonce')} : {listingTitle}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('penalties:createdAt', 'Date')} : {createdAt}
            </Typography>
          </Box>

          {/* Right: amount + chips */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            <Typography variant="h6" fontWeight={700} color="error.main">
              {amount}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Chip label={statusLabel} color={statusColor} size="small" />
              <Chip label={roleLabel}   color={roleColor}   size="small" variant="outlined" />
            </Box>
          </Box>
        </Box>

        {penalty?.reason && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {penalty.reason}
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// --------------------------------------------------------------------------
// MyPenaltiesPage
// --------------------------------------------------------------------------
const MyPenaltiesPage = () => {
  const { t }        = useTranslation(['penalties', 'common']);
  const { user }     = useAuth();
  const currentUserId = user?.id || user?.userId;

  const [penalties,       setPenalties]       = useState([]);
  const [page,            setPage]            = useState(1);
  const [limit]                               = useState(5);
  const [loading,         setLoading]         = useState(false);
  const [initialLoading,  setInitialLoading]  = useState(true);
  const [hasMore,         setHasMore]         = useState(true);
  const [totalItems,      setTotalItems]      = useState(0);
  const [error,           setError]           = useState(null);
  const [filters, setFilters] = useState({
    startDate: '', endDate: '', status: '', penaltyId: '', listingTitle: ''
  });

  const filtersRef    = useRef(filters);
  const penaltiesRef  = useRef([]);
  const fetchGenRef   = useRef(0);

  // ---- normalise various server response shapes ---------------------------
  const normalizeResponse = (response) => {
    if (!response) return { items: [], total: 0, hasNext: null };
    if (Array.isArray(response)) return { items: response, total: response.length, hasNext: null };

    const hasNext = typeof response?.pagination?.hasNext === 'boolean'
      ? response.pagination.hasNext : null;

    const arr = Array.isArray(response.data)    ? response.data
              : Array.isArray(response.results) ? response.results
              : [];

    const rawTotal = response.total
      ?? response.pagination?.total
      ?? response.pagination?.total_items
      ?? arr.length;

    const total = Math.max(Number(rawTotal) || 0, arr.length);
    return { items: arr, total, hasNext };
  };

  // ---- build query params -------------------------------------------------
  const buildParams = (p, f = {}) => {
    const params = { page: p, limit };
    if (f.status)      params.status = f.status;
    if (f.penaltyId)   params.penalty_id = f.penaltyId;
    if (f.listingTitle) params.listing_title = f.listingTitle;

    const fmt = (d) => {
      if (!d) return null;
      if (typeof d === 'string') return d;
      if (d instanceof Date) return d.toISOString().split('T')[0];
      return d;
    };
    const s = fmt(f.startDate);
    const e = fmt(f.endDate);
    if (s) params.start_date = s;
    if (e) params.end_date   = e;
    return params;
  };

  // ---- core fetch ---------------------------------------------------------
  const fetchPage = useCallback(async (p, providedFilters = null) => {
    const myGen = ++fetchGenRef.current;
    setLoading(true);
    setError(null);
    try {
      const effectiveFilters = providedFilters ?? filtersRef.current ?? {};
      const params   = buildParams(p, effectiveFilters);
      const response = await penaltyService.listUser(params);

      if (myGen !== fetchGenRef.current) return;

      const { items, total, hasNext } = normalizeResponse(response);
      setTotalItems(total);

      const existingIds = new Set(
        penaltiesRef.current.map(d => d.id || d._id || d.penalty_id)
      );
      const newItems     = items.filter(it => !existingIds.has(it.id || it._id || it.penalty_id));
      const nextList     = [...penaltiesRef.current, ...newItems];
      penaltiesRef.current = nextList;
      setPenalties(nextList);

      const computedHasMore = hasNext !== null ? hasNext : nextList.length < total;
      setHasMore(newItems.length === 0 ? false : computedHasMore);
    } catch (err) {
      if (myGen !== fetchGenRef.current) return;
      console.error('Failed to load penalties:', err);
      setError(t('penalties:list.error', 'Erreur lors du chargement des pénalités'));
      setHasMore(false);
    } finally {
      if (myGen === fetchGenRef.current) {
        setLoading(false);
        if (p === 1) setInitialLoading(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, t]);

  // keep ref in sync
  useEffect(() => { filtersRef.current = filters; }, [filters]);

  // auto-trigger on filter change (debounced 400 ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setPenalties([]);
      penaltiesRef.current = [];
      setTotalItems(0);
      setPage(1);
      setInitialLoading(true);
      fetchPage(1, filters);
    }, 400);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // load next page when page > 1
  useEffect(() => {
    if (page === 1) return;
    if (loading) return;
    fetchPage(page);
  }, [page, fetchPage]);

  const handleLoadMore = () => {
    if (loading || !hasMore) return;
    setPage(p => p + 1);
  };

  const handleClearFilters = () => {
    setFilters({ startDate: '', endDate: '', status: '', penaltyId: '', listingTitle: '' });
  };

  const openPenaltyUrl = (penalty) => {
    const id = penalty?.id || penalty?.penalty_id;
    if (id) window.open(`/penalties/${id}`, '_blank');
  };

  // ---- render -------------------------------------------------------------
  return (
    <Container maxWidth="lg" sx={{ py: 3, width: '85%', mx: 'auto' }}>

      {/* Header — same background as OwnerPaymentHistoryInfinite */}
      <Box sx={{ mb: 4 }}>
        <Box className="payment-history-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h4" fontWeight={700}>
              {t('penalties:all.title', 'Mes pénalités')}
            </Typography>
            <Typography variant="body1" sx={{ mt: 1, mb: 2, color: 'white' }}>
              {t('penalties:all.subtitle', 'Consultez vos pénalités, leur statut et les détails associés.')}
            </Typography>
          </Box>
          {penalties.length > 0 && (
            <div className="history-count">
              <Typography variant="body2" sx={{ color: 'inherit' }}>
                {t('penalties:all.count',
                  { count: penalties.length, total: totalItems || '?' },
                  `${penalties.length} pénalité(s) chargée(s) sur ${totalItems || '?'}`
                )}
              </Typography>
            </div>
          )}
        </Box>
        {penalties.length === 0 && !initialLoading && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('penalties:all.noData', "Vous n'avez aucune pénalité")}
          </Typography>
        )}
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center"
          sx={{ flexWrap: 'nowrap', overflowX: 'auto' }}>

          <Box sx={{ minWidth: 300 }}>
            <DateRangePicker
              startDate={filters.startDate}
              endDate={filters.endDate}
              onDateRangeChange={(start, end) =>
                setFilters(f => ({ ...f, startDate: start, endDate: end }))}
              label={t('penalties:filters.dateRange', 'Plage de dates')}
              size="small"
            />
          </Box>

          <TextField
            label={t('penalties:filters.penaltyId', 'ID pénalité')}
            value={filters.penaltyId}
            onChange={(e) => setFilters(f => ({ ...f, penaltyId: e.target.value }))}
            size="small"
            sx={{ minWidth: 200 }}
          />

          <TextField
            label={t('penalties:filters.listingTitle', "Titre de l'annonce")}
            value={filters.listingTitle}
            onChange={(e) => setFilters(f => ({ ...f, listingTitle: e.target.value }))}
            size="small"
            sx={{ minWidth: 240 }}
          />

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="penalty-status-label">
              {t('penalties:filters.status', 'Statut')}
            </InputLabel>
            <Select
              labelId="penalty-status-label"
              label={t('penalties:filters.status', 'Statut')}
              value={filters.status}
              onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
            >
              <MenuItem value="">{t('common:all', 'Tous')}</MenuItem>
              <MenuItem value="pending">{t('penalties:status.pending', 'En attente')}</MenuItem>
              <MenuItem value="applied">{t('penalties:status.applied', 'Appliquée')}</MenuItem>
              <MenuItem value="collected">{t('penalties:status.collected', 'Collectée')}</MenuItem>
              <MenuItem value="disputed">{t('penalties:status.disputed', 'Disputée')}</MenuItem>
              <MenuItem value="waived">{t('penalties:status.waived', 'Annulée')}</MenuItem>
              <MenuItem value="refunded">{t('penalties:status.refunded', 'Remboursée')}</MenuItem>
              <MenuItem value="cancelled">{t('penalties:status.cancelled', 'Annulée')}</MenuItem>
              <MenuItem value="pending_collection">
                {t('penalties:status.pending_collection', 'Collecte en attente')}
              </MenuItem>
            </Select>
          </FormControl>

          <Button variant="outlined" onClick={handleClearFilters} size="small"
            sx={{ textTransform: 'none', flexShrink: 0 }}>
            {t('common:clear', 'Effacer')}
          </Button>
        </Stack>
      </Box>

      {/* Initial loading spinner */}
      {initialLoading && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress size={48} />
          <Typography sx={{ mt: 2 }} color="text.secondary">
            {t('common:loading', 'Chargement...')}
          </Typography>
        </Box>
      )}

      {/* Error */}
      {error && !initialLoading && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {/* Penalty list */}
      {!initialLoading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {penalties.map((p) => (
            <PenaltyListCard
              key={p.id || p.penalty_id}
              penalty={p}
              currentUserId={currentUserId}
              onOpen={openPenaltyUrl}
            />
          ))}
        </Box>
      )}

      {/* Load more button */}
      {!initialLoading && penalties.length > 0 && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="outlined"
            onClick={handleLoadMore}
            disabled={loading || !hasMore}
            sx={{ textTransform: 'none' }}
          >
            {loading
              ? t('common:loading', 'Chargement...')
              : t('common:loadMore', 'Charger plus')}
          </Button>
        </Box>
      )}

      {/* Inline loading indicator for next page */}
      {!initialLoading && loading && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {/* End-of-list message */}
      {!hasMore && !initialLoading && !loading && penalties.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            ✅ {t('penalties:all.end', 'Toutes les pénalités ont été chargées')}
          </Typography>
        </Box>
      )}

      {/* Empty state */}
      {!hasMore && !initialLoading && penalties.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" color="text.secondary">
            {t('penalties:all.noData', "Vous n'avez aucune pénalité")}
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default MyPenaltiesPage;
